import { useIntersectionObserver } from '@folklore/hooks';
import classNames from 'classnames';

import RemaxCard from './RemaxCard';

import styles from '../../styles/animations/remax/budget-summary.module.css';

type BudgetSummaryProps = {
    config?: string | null;
    title?: string | null;
    className?: string | null;
};

function BudgetSummary({ config: configStr, title, className }: BudgetSummaryProps) {
    let config = null;
    try {
        const clean = (configStr || '').replace(/&quot;/g, '"');
        config = JSON.parse(clean);
    } catch {
        config = null;
    }

    const {
        ref: containerRef,
        entry: { isIntersecting = false },
    } = useIntersectionObserver({ rootMargin: '-10%' });

    const {
        leftValue = null,
        leftLabel = null,
        rightValue = null,
        rightLabel = null,
        resultValue = null,
        resultLabel = null,
        format = null,
    } = config || {};

    const finalFormat = {
        minimumFractionDigits: 0,
        ...(format || {}),
    };
    const formatter = new Intl.NumberFormat('fr-CA', finalFormat);
    const formattedLeftValue = leftValue !== null ? formatter.format(leftValue) : null;
    const formattedRightValue = rightValue !== null ? formatter.format(rightValue) : null;
    const formattedResultValue = resultValue !== null ? formatter.format(resultValue) : null;

    const total = rightValue;
    const leftWidth = leftValue / total;
    const rightWidth = 1 - leftWidth;

    return (
        <RemaxCard title={title} className={styles.card}>
            <div
                className={classNames([
                    styles.container,
                    className,
                    { [styles.visible]: isIntersecting },
                ])}
                ref={containerRef}
            >
                <div className={styles.chart}>
                    <div
                        className={classNames(styles.section, styles.left)}
                        style={{ width: `${leftWidth * 100}%` }}
                    >
                        <div className={styles.label}>
                            <div>{leftLabel}</div>
                            <div className={styles.value}>{formattedLeftValue}</div>
                        </div>
                        <div className={classNames(styles.bar, styles.top)} />
                        <div className={styles.separator} />
                        <div className={classNames(styles.bar, styles.bottom)} />
                    </div>
                    <div
                        className={classNames(styles.section, styles.right)}
                        style={{ width: `${rightWidth * 100}%` }}
                    >
                        <div className={styles.label}>
                            <div>{rightLabel}</div>
                            <div className={styles.value}>{formattedRightValue}</div>
                        </div>
                        <div className={classNames(styles.bar, styles.top)} />
                        <div className={styles.separator} />
                        <div className={classNames(styles.bar, styles.bottom)} />
                        <div className={styles.downLine} />
                    </div>
                </div>
                <div className={styles.result}>
                    <div className={styles.label}>{resultLabel}</div>
                    <div className={styles.value}>{formattedResultValue}</div>
                </div>
            </div>
        </RemaxCard>
    );
}

export default BudgetSummary;
